import React, { Suspense, lazy } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeProvider } from './contexts/ThemeContext/ThemeProvider';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { Toaster } from 'react-hot-toast';

// Pages
import Login from './pages/auth/Login';
import TeacherRegister from './pages/auth/TeacherRegister';
import UnifiedRegistrationPageSimple from './pages/auth/UnifiedRegistrationPageSimple';
import ChangePassword from './pages/auth/ChangePassword';
import ForgotPassword from './pages/auth/ForgotPassword';
import Dashboard from './pages/dashboard/Dashboard';
import LandingPage from './pages/LandingPage';
import { MethodNotAllowedPage, NotFoundPage } from './pages/error';

// Debug Pages
import CodeDebugPage from './pages/debug/CodeDebugPage';

// Product Pages
import Features from './pages/product/Features';
import Pricing from './pages/product/Pricing';
import PaymentSuccess from './pages/product/PaymentSuccess';

// Support Pages
import SupportCenter from './pages/support/SupportCenter';
import ContactUs from './pages/support/ContactUs';
import FAQ from './pages/support/FAQ';

// Company Pages
import AboutUs from './pages/company/AboutUs';
import Documentation from './pages/company/Documentation';
import PrivacyPolicy from './pages/company/PrivacyPolicy';
import TermsOfService from './pages/company/TermsOfService';
import ZoomDocumentation from './pages/company/ZoomDocumentation';

// HR Pages
import {
  EmployeesManagement,
  RolesManagement,
  AttendanceManagement,
} from './pages/hr';
import HRPage from './pages/HRPage';
import DepartmentEmployeesPage from './pages/hr/DepartmentEmployeesPage';

// Coupons Pages
import CouponsManagement from './pages/coupons/CouponsManagement';
import ReferralSystem from './pages/coupons/ReferralSystem';
import UserCouponsPage from './pages/coupons/UserCouponsPage';

// Test Pages
import TestDataPage from './pages/TestDataPage';

// Components
import ProtectedRoute from './components/auth/ProtectedRoute';
import { isSystemManager } from './utils/authorization';
import LoadingSpinner from './components/ui/LoadingSpinner';
import OfflineBanner from './components/common/OfflineBanner';
import InstallPrompt from './components/common/InstallPrompt';

// Subscription
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import SubscriptionPage from './pages/subscription/SubscriptionPage';

// Hooks
import { useAuth } from './hooks/useAuth';
import AccountPreferencesSync from './components/settings/AccountPreferencesSync';

// Role and account pages are split from the public entry bundle.
const TeacherDashboard = lazy(() => import('./pages/teacher/TeacherDashboard'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const SupportTicketsPage = lazy(() => import('./pages/admin/SupportTicketsPage'));
const StudentDashboard = lazy(() => import('./pages/student/StudentDashboard'));
const ParentDashboard = lazy(() => import('./pages/parent/ParentDashboard'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const AccountSettings = lazy(() => import('./pages/AccountSettings'));
const ProfileRedirect = lazy(() => import('./pages/ProfileRedirect'));
const SecurityPage = lazy(() => import('./pages/SecurityPage'));
const StandaloneEmployeeDashboard = lazy(() =>
  import('./pages/employee').then(module => ({
    default: module.EmployeeDashboard,
  }))
);
const ChatPage = lazy(() => import('./pages/chat/ChatPage'));

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SubscriptionProvider>
        <CurrencyProvider>
          <LanguageProvider>
            <AccountPreferencesSync />
            <NotificationProvider>
              <Router>
                <div
                  className='min-h-screen bg-brand-background dark:bg-gray-900'
                >
                  <Suspense
                    fallback={
                      <div className='flex min-h-screen items-center justify-center'>
                        <LoadingSpinner size='large' />
                      </div>
                    }
                  >
                  <Routes>
                    {/* Public Routes */}
                    <Route path='/home' element={<LandingPage />} />
                    <Route path='/login' element={<Login />} />
                    <Route
                      path='/register'
                      element={<UnifiedRegistrationPageSimple />}
                    />
                    <Route
                      path='/register/old'
                      element={<Navigate to='/register' replace />}
                    />
                    <Route
                      path='/register/old2'
                      element={<Navigate to='/register' replace />}
                    />
                    <Route
                      path='/register/:role'
                      element={<Navigate to='/register' replace />}
                    />
                    <Route
                      path='/register-teacher'
                      element={<TeacherRegister />}
                    />
                    <Route
                      path='/forgot-password'
                      element={<ForgotPassword />}
                    />

                    {/* Test Pages */}
                    <Route path='/test-data' element={<TestDataPage />} />

                    {/* Product Pages */}
                    <Route path='/features' element={<Features />} />
                    <Route path='/pricing' element={<Pricing />} />
                    <Route path='/payment-success' element={<PaymentSuccess />} />

                    {/* Support Pages */}
                    <Route path='/support' element={<SupportCenter />} />
                    <Route path='/contact' element={<ContactUs />} />
                    <Route path='/faq' element={<FAQ />} />

                    {/* Company Pages */}
                    <Route path='/about' element={<AboutUs />} />
                    <Route path='/documentation' element={<Documentation />} />
                    <Route path='/zoom-documentation' element={<ZoomDocumentation />} />
                    <Route path='/privacy' element={<PrivacyPolicy />} />
                    <Route path='/terms' element={<TermsOfService />} />

                    {/* Protected Routes */}
                    <Route
                      path='/dashboard'
                      element={
                        <ProtectedRoute>
                          <DashboardRouter />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path='/teacher/dashboard'
                      element={
                        <ProtectedRoute requiredRole='teacher'>
                          <TeacherDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path='/admin/dashboard'
                      element={
                        <ProtectedRoute requiredRole='admin'>
                          <AdminDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path='/student/dashboard'
                      element={
                        <ProtectedRoute requiredRole='student'>
                          <StudentDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path='/parent/dashboard'
                      element={
                        <ProtectedRoute requiredRole='parent'>
                          <ParentDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path='/profile'
                      element={
                        <ProtectedRoute>
                          <ProfileRedirect />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path='/notifications'
                      element={
                        <ProtectedRoute>
                          <NotificationsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path='/settings'
                      element={
                        <ProtectedRoute>
                          <AccountSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path='/change-password'
                      element={
                        <ProtectedRoute>
                          <ChangePassword />
                        </ProtectedRoute>
                      }
                    />

                    {/* Chat Routes */}
                    <Route
                      path='/chat'
                      element={
                        <ProtectedRoute>
                          <ChatPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Coupons Routes */}
                    <Route
                      path='/coupons'
                      element={
                        <ProtectedRoute>
                          <UserCouponsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path='/referral'
                      element={
                        <ProtectedRoute>
                          <ReferralSystem />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path='/admin/coupons'
                      element={
                        <ProtectedRoute requiredRole='admin'>
                          <CouponsManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path='/admin/support-tickets'
                      element={
                        <ProtectedRoute requiredRole='admin'>
                          <SupportTicketsPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* HR Routes */}
                    <Route
                      path='/hr'
                      element={
                        <ProtectedRoute
                          allowedRoles={['teacher', 'admin', 'employee']}
                          requiredPermission='canAccessHR'
                        >
                          <HRPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path='/hr/employees'
                      element={
                        <ProtectedRoute
                          allowedRoles={['teacher', 'admin', 'employee']}
                          requiredPermission='canAccessHR'
                        >
                          <EmployeesManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path='/hr/departments'
                      element={
                        <ProtectedRoute
                          allowedRoles={['teacher', 'admin', 'employee']}
                          requiredPermission='canAccessHR'
                        >
                          <DepartmentEmployeesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path='/hr/roles'
                      element={
                        <ProtectedRoute allowedRoles={['teacher', 'admin']}>
                          <RolesManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path='/hr/attendance'
                      element={
                        <ProtectedRoute
                          allowedRoles={['teacher', 'admin', 'employee']}
                          requiredPermission='canAccessHR'
                        >
                          <AttendanceManagement />
                        </ProtectedRoute>
                      }
                    />

                    {/* Employee Routes */}
                    <Route
                      path='/employee/dashboard'
                      element={
                        <ProtectedRoute requiredRole='employee'>
                          <StandaloneEmployeeDashboard />
                        </ProtectedRoute>
                      }
                    />

                    {/* Security Routes */}
                    <Route
                      path='/security'
                      element={
                        <ProtectedRoute allowedRoles={['teacher', 'admin']}>
                          <SecurityPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Debug Routes - للمطورين فقط */}
                    <Route
                      path='/debug/codes'
                      element={
                        <ProtectedRoute>
                          <CodeDebugPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Subscription Route */}
                    <Route
                      path='/subscription'
                      element={
                        <ProtectedRoute requiredRole='teacher'>
                          <SubscriptionPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Default Route */}
                    <Route path='/' element={<Navigate to='/home' replace />} />

                    {/* Error Routes */}
                    <Route path='/404' element={<NotFoundPage />} />
                    <Route path='/405' element={<MethodNotAllowedPage />} />

                    {/* 404 Route */}
                    <Route path='*' element={<NotFoundPage />} />
                  </Routes>
                  </Suspense>

                  {/* PWA Components */}
                  <OfflineBanner />
                  <InstallPrompt />

                  {/* Toast Notifications */}
                  <Toaster
                    position='bottom-left'
                    toastOptions={{
                      duration: 4000,
                      style: {
                        background: '#fff',
                        color: '#1f2937',
                        fontFamily:
                          'Alexandria, Noto Sans Arabic, Arial, sans-serif',
                        direction: 'rtl',
                        textAlign: 'right',
                      },
                    }}
                  />
                </div>
              </Router>
            </NotificationProvider>
          </LanguageProvider>
        </CurrencyProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

// Dashboard Router Component
const DashboardRouter: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <LoadingSpinner size='large' />
      </div>
    );
  }

  if (!user) {
    return <Navigate to='/login' replace />;
  }

  // Route based on user role
  switch (user.role) {
    case 'teacher':
      return <TeacherDashboard />;
    case 'student':
      return <StudentDashboard />;
    case 'parent':
      return <ParentDashboard />;
    case 'admin':
      return isSystemManager(user) ? (
        <AdminDashboard />
      ) : (
        <Navigate to='/login' replace />
      );
    case 'employee':
      return <StandaloneEmployeeDashboard />; // Employee uses dedicated standalone dashboard
    default:
      return <Dashboard />;
  }
};

export default App;
